import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CONFIGURATION_STATUS, CONFIGURATION_STATUS_TYPE } from 'src/shared/constants/constants'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import { useParams } from 'react-router'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Option } from 'src/shared/form/control'
import { Form } from 'react-final-form'
import { Milestone_Config, Milestone_Config_Set_Input, Mutation_Root, Query_Root } from 'src/@types/graphql'
import {
  queryMilestoneConfigurationById,
  updateMilestoneConfigById,
} from 'src/screens/administration/configuration-management/milestones-configuration/milestoneConfigQuerties'
import {
  MILESTONE_CONFIG_FREQUENCY,
  MILESTONE_CONFIG_FREQUENCY_TYPE,
  MILESTONE_RESPONSIBLE_TYPE,
  MILESTONE_RESPONSIBLE_TYPE_TYPE,
  MILESTONE_TYPE,
  MILESTONE_TYPE_TYPE,
} from 'src/shared/constants/milestone-constants'
import { Alert, Grid2, Stack, Typography } from '@mui/material'
import { DateTimePickerField } from 'src/shared/form/control/DateTimePickerField'
import { required, validDate, validPositiveNumber } from 'src/shared/form/validation/validators'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { TextField } from 'src/shared/form/control/TextField'
import { NumberField } from 'src/shared/form/control/NumberField'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { originType } from 'src/screens/shared/implementation/details/milestone/details/default-milestone/edit/MilestoneDetailsEditForm'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import {
  getConfigStatusOptions,
  getTranslationForMilestoneFrequency,
  getTranslationForMilestoneResponsibleType,
  getTranslationForMilestoneType,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { RadioButtonGroupField, RadioData } from 'src/shared/form/control/RadioButtonGroupField'
import { getMilestoneConfigProcessOptions } from 'src/screens/administration/configuration-management/milestones-configuration/MilestoneConfigUtils'

export interface MilestoneConfigFormValues {
  type: string
  enDesc: string
  deDesc: string
  frDesc: string
  itDesc: string
  status: CONFIGURATION_STATUS_TYPE
  process: string
  frequency: MILESTONE_CONFIG_FREQUENCY_TYPE
  due?: string | null
  taskMonth: number
  responsible: MILESTONE_RESPONSIBLE_TYPE_TYPE
  workflow: string
}

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

export const MilestoneConfigurationEditPage = () => {
  const { milestoneConfigId } = useParams()

  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()
  const originRef = useRef<OriginType>('SAVE')

  const [initialValues, setInitialValues] = useState<MilestoneConfigFormValues | null>(null)
  const [milestoneType, setMilestoneType] = useState<MILESTONE_TYPE_TYPE>()

  const processAllocOptions: RadioData[] = useMemo(() => getMilestoneConfigProcessOptions(getMessage), [getMessage])

  // mappers
  const mapToFormValues = useCallback(
    (milestoneConfig: Milestone_Config): MilestoneConfigFormValues => ({
      type: getTranslationForMilestoneType(getMessage, milestoneConfig.type),
      enDesc: milestoneConfig.description['en'],
      deDesc: milestoneConfig.description['de'],
      frDesc: milestoneConfig.description['fr'],
      itDesc: milestoneConfig.description['it'],
      status: milestoneConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      process: milestoneConfig.process,
      frequency: milestoneConfig.frequency as MILESTONE_CONFIG_FREQUENCY_TYPE,
      due:
        milestoneConfig.month && milestoneConfig.day
          ? DateUtils.formatDate(new Date(new Date().getFullYear(), milestoneConfig.month! - 1, milestoneConfig.day!))
          : undefined,
      taskMonth: Math.abs(Number(milestoneConfig.task_months)),
      responsible: milestoneConfig.initial_responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
      workflow: getMessage(`label.milestone.config.workflow.${milestoneConfig.workflow_type}`),
    }),
    [getMessage],
  )

  const mapToMilestoneConfig = useCallback((milestoneConfig: MilestoneConfigFormValues): Milestone_Config_Set_Input => {
    const dueDate = milestoneConfig.due ? DateUtils.parseDate(milestoneConfig.due) : undefined

    const input: Milestone_Config_Set_Input = {
      active: milestoneConfig.status === CONFIGURATION_STATUS.ACTIVE,
      description: {
        de: milestoneConfig.deDesc,
        en: milestoneConfig.enDesc,
        fr: milestoneConfig.frDesc,
        it: milestoneConfig.itDesc,
      },
      day: dueDate ? dueDate?.getDate() : null,
      month: dueDate ? dueDate.getMonth() + 1 : null,
      frequency: milestoneConfig.frequency,
      task_months: milestoneConfig.taskMonth === 0 ? 0 : -Math.abs(Number(milestoneConfig.taskMonth)),
      initial_responsible_type: milestoneConfig.responsible,
    }

    return input
  }, [])

  // fetch data
  const fetchMilestoneConfig = useCallback(async () => {
    if (!milestoneConfigId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{
        milestone_config_by_pk: Query_Root['milestone_config_by_pk']
      }>(queryMilestoneConfigurationById, { milestoneConfigId })
      .toPromise()

    if (error || !data?.milestone_config_by_pk) {
      throw new Error('Resource not found')
    }

    const milestoneConfig = data.milestone_config_by_pk

    const formValues = mapToFormValues(milestoneConfig)

    setMilestoneType(milestoneConfig.type as MILESTONE_TYPE_TYPE)

    return { formValues }
  }, [milestoneConfigId, mapToFormValues, urqlClient])

  useEffect(() => {
    fetchMilestoneConfig()
      .then((res) => {
        setInitialValues(res.formValues)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchMilestoneConfig, notificationService])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.MilestonesConfigurationIndex.params({}))
  }, [navigate])

  // fields options
  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const frequencyTypeOptions: Option[] = useMemo(
    () => [
      ...Object.entries(MILESTONE_CONFIG_FREQUENCY).map(([value]) => ({
        label: getTranslationForMilestoneFrequency(getMessage, value),
        value: value as MILESTONE_CONFIG_FREQUENCY_TYPE,
      })),
    ],
    [getMessage],
  )

  const responsibleTypeOptions: Option[] = useMemo(
    () => [
      ...Object.entries(MILESTONE_RESPONSIBLE_TYPE).map(([value]) => ({
        label: getTranslationForMilestoneResponsibleType(getMessage, value),
        value: value as MILESTONE_CONFIG_FREQUENCY_TYPE,
      })),
    ],
    [getMessage],
  )

  // handlers
  const handleUpdate = async (values: MilestoneConfigFormValues) => {
    const input = mapToMilestoneConfig(values)

    const { data, error } = await urqlClient
      .mutation<{
        update_milestone_config: Mutation_Root['update_milestone_config']
      }>(updateMilestoneConfigById, {
        id: milestoneConfigId,
        input,
      })
      .toPromise()

    if (!error && data?.update_milestone_config?.affected_rows === 1) {
      notificationService.changesSaved()
      setInitialValues(values)
      if (originRef.current === 'SAVE_AND_BACK') {
        onBackHandler()
      }
    } else {
      notificationService.operationFailed()
    }
  }

  const onSubmit = (origin: originType) => (_: any) => {
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('milestone-config-form')?.dispatchEvent(
      new Event('submit', {
        cancelable: true,
        bubbles: true,
      }),
    )
  }

  //
  const allowDueDateEdit = useMemo(() => {
    if (milestoneType) {
      return milestoneType !== MILESTONE_TYPE.CONTRACT && milestoneType !== MILESTONE_TYPE.FINAL_REPORT
    } else {
      return true
    }
  }, [milestoneType])

  return (
    <ScreenLayout
      title={getMessage('label.milestone.config.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <Stack spacing={3}>
              <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>

              <Form<MilestoneConfigFormValues>
                initialValues={initialValues}
                onSubmit={handleUpdate}
                decorators={decorators}
                render={({ handleSubmit }) => {
                  return (
                    <form id="milestone-config-form" onSubmit={handleSubmit} noValidate>
                      <Stack spacing={4} mt={4}>
                        <Grid2 container columnSpacing={5}>
                          <Grid2 size={6}>
                            <AutoCompleteField
                              options={statusFieldOptions}
                              name="status"
                              label={getMessage('label.status')}
                              disableClearable={true}
                              validate={required()}
                            />
                          </Grid2>

                          <Grid2 size={6}>
                            <TextField name="type" disabled={true} label={getMessage('label.type')} />
                          </Grid2>

                          <Grid2 size={6}>
                            <TextField
                              name="workflow"
                              disabled={true}
                              label={getMessage('label.milestone.config.workflow')}
                            />
                          </Grid2>
                        </Grid2>

                        <Typography variant="h2">{getMessage('label.milestone.config.deadline')}</Typography>

                        <Grid2 container columnSpacing={5}>
                          <Grid2 size={allowDueDateEdit ? 4 : 6}>
                            <AutoCompleteField
                              options={frequencyTypeOptions}
                              name="frequency"
                              label={getMessage('label.milestone.config.frequency')}
                              disableClearable={true}
                              disabled={true}
                              required
                            />
                          </Grid2>

                          {allowDueDateEdit && (
                            <Grid2 size={4}>
                              <DateTimePickerField
                                label={getMessage('label.milestone.config.due')}
                                name="due"
                                views={['month', 'day']}
                                openToView={'month'}
                                forwardSlotProps={{
                                  actionBar: {
                                    actions: ['clear'],
                                  },
                                }}
                                validate={validDate()}
                              />
                            </Grid2>
                          )}

                          <Grid2 size={allowDueDateEdit ? 4 : 6}>
                            <NumberField
                              name="taskMonth"
                              label={getMessage('label.milestone.config.task.months')}
                              required
                              validate={validPositiveNumber(undefined, true)}
                            />
                          </Grid2>
                        </Grid2>

                        <Typography variant="h2">{getMessage('label.description')}</Typography>

                        <Grid2 container columnSpacing={5}>
                          <Grid2 size={6}>
                            <TextField
                              name="deDesc"
                              label={getMessage('label.description.de')}
                              required
                              validate={required()}
                            />
                          </Grid2>
                          <Grid2 size={6}>
                            <TextField
                              name="enDesc"
                              label={getMessage('label.description.en')}
                              required
                              validate={required()}
                            />
                          </Grid2>
                          <Grid2 size={6}>
                            <TextField
                              name="frDesc"
                              label={getMessage('label.description.fr')}
                              required
                              validate={required()}
                            />
                          </Grid2>
                          <Grid2 size={6}>
                            <TextField
                              name="itDesc"
                              label={getMessage('label.description.it')}
                              required
                              validate={required()}
                            />
                          </Grid2>
                        </Grid2>

                        <Typography variant="h2">{getMessage('label.configuration.grid.allocation')}</Typography>

                        <Grid2 container columnSpacing={5}>
                          <Grid2 size={6}>
                            <AutoCompleteField
                              options={responsibleTypeOptions}
                              name="responsible"
                              label={getMessage('label.milestone.config.responsible')}
                              disableClearable={true}
                              validate={required()}
                            />
                          </Grid2>

                          <Grid2 size={12}>
                            <RadioButtonGroupField
                              disabled={true}
                              name="process"
                              color="primary"
                              required
                              label={
                                <Typography variant={'subtitle2'} component="span" sx={{ ml: -2 }}>
                                  {getMessage('label.process')}
                                </Typography>
                              }
                              data={processAllocOptions}
                            />
                          </Grid2>
                        </Grid2>
                        <DirtyFormSpy />
                      </Stack>
                    </form>
                  )
                }}
              />
            </Stack>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
